<script setup>
import localStorageKeys from '@/constants/local-storage-keys';

import { authEmailValidationSchema } from '@/config/validation-schema';

const props = defineProps({
  title: {
    type: Function,
    default: undefined,
  },
  text: {
    type: Function,
    default: undefined,
  },
  buttonText: {
    type: Function,
    default: undefined,
  },

  isSending: {
    type: Boolean,
    default: false,
  },

  autocompleteEmail: {
    type: Boolean,
    default: false,
  },

  errors: {
    type: Object,
    default: () => ({}),
  },
});
const emit = defineEmits(['submit']);

const { t } = useI18n();

const formRef = ref(null);
const isFormSending = ref(props.isSending);

const userEmail = useLocalStorage(localStorageKeys.USER_EMAIL, undefined);
const email = ref(
  props.autocompleteEmail && userEmail.value ? userEmail.value : '',
);

function handleSubmit() {
  if (isFormSending.value) return;

  isFormSending.value = true;

  emit('submit', email.value);
}

watch(
  () => props.isSending,
  (value) => {
    isFormSending.value = value;
  },
);

watch(
  () => props.errors,
  (errors) => {
    if (!formRef.value) return;
    formRef.value.setErrors(errors);
  },
  { deep: true },
);

const validationSchema = computed(() => authEmailValidationSchema(t));
</script>

<template>
  <ModalBase class="modals-email" :title="title?.()" :header-mode="2">
    <div class="modals-email__container">
      <div v-if="text" class="modals-email__text">
        <span
          class="modals-email__font modals-email__font--text"
          v-html="text()"
        />
      </div>

      <VeeForm
        ref="formRef"
        v-slot="{ meta: { valid } }"
        :validation-schema="validationSchema"
        @submit="handleSubmit"
      >
        <FormFieldInput
          v-model="email"
          class="modals-email__input"
          :placeholder="t('header.yourEmail')"
          name="email"
          text-align="center"
          theme="underline"
          autocapitalize="none"
        />

        <UiButton
          class="modals-email__button"
          type="submit"
          theme="purple"
          :disabled="!valid || isFormSending"
        >
          <span class="modals-email__font modals-email__font--button">
            {{ buttonText?.() }}
          </span>
        </UiButton>
      </VeeForm>
    </div>
  </ModalBase>
</template>

<style scoped lang="scss">
.modals-email {
  &__font {
    display: block;

    &--text {
      font-size: em(18);
      line-height: math.div(22, 18);
    }

    &--button {
      font-size: em(20);
      font-weight: 500;
      line-height: math.div(22, 20);
    }
  }

  &__icon {
    @include box(em(34));

    flex-shrink: 0;
    margin-bottom: em(16);
    color: $color-second-1;
  }

  &__container {
    min-width: em(387);
    padding: 0 em(14) em(44);

    @include media-breakpoint-down(sm) {
      padding-right: em($gap-sm);
      padding-left: em($gap-sm);
    }
  }

  &__text {
    margin-bottom: em(20);
    color: $color-gray-13;
    text-align: center;
  }

  &__input {
    margin-bottom: em(36);

    @include media-breakpoint-down(sm) {
      margin-bottom: em(41);
    }
  }

  &__button {
    width: 100%;
    height: em(63);
    margin: 0 auto;

    @include media-breakpoint-down(sm) {
      width: 100%;
    }
  }
}
</style>
